import { numberParam, optionalNumberParam } from '../types/checks.js';
import type { CheckResult, ValidationCheck } from '../types/checks.js';
import { missingRequirementReason, notChecked, plainResult, withRequirement } from './support.js';

const formatBytes = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} Б`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1).replace('.', ',')} КБ`;
  return `${(bytes / (1024 * 1024)).toFixed(2).replace('.', ',')} МБ`;
};

/**
 * File size check.
 *
 * Two very different bounds live here and must never be confused:
 *
 *   maxBytes       — an official upload limit (e.g. 240 KB for the DS-160 form);
 *   warnBelowBytes — our own quality heuristic: a tiny file usually means heavy
 *                    compression. The authority does not require a minimum, so
 *                    this can only ever produce a `warning`, never a failure.
 */
export const fileSizeCheck: ValidationCheck = {
  id: 'file_size',
  title: 'Размер файла',
  description: 'Сравнивает вес файла с официальными пределами и внутренним порогом качества.',
  kind: 'technical',
  requires: ['fileInfo'],

  run: (ctx): CheckResult => {
    const missing = missingRequirementReason(ctx, ['fileInfo']);
    if (missing) return notChecked(ctx, fileSizeCheck, missing, ctx.rule.severity);

    const maxBytes = numberParam(ctx.rule.params, 'maxBytes', ctx.rule.official?.max ?? 245760);
    const officialMin = optionalNumberParam(ctx.rule.params, 'minBytes') ?? ctx.rule.official?.min;
    const warnBelow = optionalNumberParam(ctx.rule.params, 'warnBelowBytes');
    const size = ctx.fileInfo.sizeBytes;

    if (size === undefined) {
      return plainResult({
        id: fileSizeCheck.id,
        title: fileSizeCheck.title,
        status: 'not_checked',
        severity: ctx.rule.severity,
        message: 'Размер файла неизвестен (снимок сделан камерой внутри валидатора).',
        fixability: 'none',
        details: { maxBytes, minBytes: officialMin ?? null, warnBelowBytes: warnBelow ?? null },
      });
    }

    const tooLarge = size > maxBytes;
    const officialTooSmall = officialMin !== undefined && size < officialMin;
    const suspiciouslySmall = !officialTooSmall && warnBelow !== undefined && size < warnBelow;

    const status = tooLarge || officialTooSmall ? 'fail' : suspiciouslySmall ? 'warning' : 'pass';

    const requirementParts = [`не более ${formatBytes(maxBytes)}`];
    if (officialMin !== undefined) requirementParts.push(`не менее ${formatBytes(officialMin)}`);

    const message = (() => {
      if (tooLarge) {
        return `Файл слишком большой: ${formatBytes(size)} при официальном пределе ${formatBytes(maxBytes)}.`;
      }
      if (officialTooSmall) {
        return `Файл меньше официального минимума: ${formatBytes(size)} при ${formatBytes(officialMin ?? 0)}.`;
      }
      if (suspiciouslySmall) {
        return `Файл очень маленький (${formatBytes(size)}): возможна сильная компрессия, детали лица могут быть потеряны. Это не официальное требование, а наша рекомендация.`;
      }
      return `Размер файла ${formatBytes(size)} — в допустимых пределах.`;
    })();


    const result = plainResult({
      id: fileSizeCheck.id,
      title: fileSizeCheck.title,
      status,
      severity: ctx.rule.severity,
      value: size,
      ...(officialMin !== undefined ? { min: officialMin } : {}),
      max: maxBytes,
      unit: 'bytes',
      message,
      requirement: `Размер файла: ${requirementParts.join(', ')}.`,
      fixability: 'fixable',
      confidence: 1,
      details: {
        maxBytes,
        minBytes: officialMin ?? null,
        warnBelowBytes: warnBelow ?? null,
        policyViolation: suspiciouslySmall,
        policy: ctx.rule.policy ?? null,
        displayValue: formatBytes(size),
      },
    });

    return withRequirement(result, ctx.rule, { unit: 'bytes' });
  },
};
